import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { deleteRecord, fetchRecords } from '../services/OrdersService';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

// Module-scoped global filter state (shared across components)
let globalFilters: Record<string, string> = { status: 'PENDING' };
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useOrdersFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

export const useOrders = (context: 'customer' | 'reports' = 'customer') => {
    const queryClient = useQueryClient();
    const { filters: userFilters } = useOrdersFilter();
    const [keyword, setKeyword] = useState('');
    const [limit] = useState(10);
    const [selection] = useState<Record<string, boolean>>({});
    const { id: customerId } = useParams();

    const sort = { column: 'created', direction: 'desc' as const };

    // Merge contextual filter (customerId from URL) with user-applied filters
    const mergedFilter: Record<string, string> = {
        ...userFilters,
        ...((context === 'customer' && customerId) ? { customerId } : {})
    };

    const ordersQuery = useInfiniteQuery({
        queryKey: ['orders', keyword, mergedFilter, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit, sort, filter: mergedFilter, nextToken: pageParam }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Order deleted');
            queryClient.invalidateQueries({ queryKey: ['orders'] });
        },
        onError: notifyError
    });

    const fetchRecordsAction = () => {
        queryClient.invalidateQueries({ queryKey: ['orders'] });
    };

    const fetchMoreRecords = () => {
        if (ordersQuery.hasNextPage) {
            ordersQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = ordersQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: ordersQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: ordersQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsAction,
        fetchMoreRecords,
        deleteRecords,
        handleFilterByKeyword,
        deleteMutation
    };
};
